import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, Zap, Loader2, ImagePlus, AlertTriangle } from 'lucide-react';
import { useEmpPlanilhaVersoes, useEmpPlanilhaDetalhes } from '@/hooks/useEmpPlanilha';
import { useEmpExecucaoMutations } from '@/hooks/useEmpExecucao';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUxAnalyticsFlag } from '@/hooks/useUxAnalyticsFlag';

interface QuickCaptureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contratoId: string;
}

export const QuickCaptureModal: React.FC<QuickCaptureModalProps> = ({ open, onOpenChange, contratoId }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { logUxEvent } = useUxAnalyticsFlag();
  const { createExecucao, uploadEvidencia } = useEmpExecucaoMutations(contratoId);
  const { data: versoes = [] } = useEmpPlanilhaVersoes(contratoId);
  const versaoVigente = versoes.find(v => v.status === 'vigente');
  const planilhaData = useEmpPlanilhaDetalhes(versaoVigente?.id ?? null);
  const itens = planilhaData.itens;

  // Frentes
  const { data: frentes = [] } = useQuery({
    queryKey: ['emp-frentes-quick', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_frentes_trabalho')
        .select('id, nome')
        .eq('contrato_id', contratoId)
        .eq('status', 'ativa')
        .order('nome');
      if (error) throw error;
      return data as { id: string; nome: string }[];
    },
    enabled: !!contratoId && open,
  });

  // Última frente usada pelo usuário neste contrato
  const { data: ultimaFrente } = useQuery({
    queryKey: ['emp-ultima-frente', contratoId, user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('emp_validacoes_execucao')
        .select('frente_id')
        .eq('contrato_id', contratoId)
        .eq('registrado_por', user.id)
        .not('frente_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data?.frente_id ?? null;
    },
    enabled: !!contratoId && !!user && open,
  });

  const [itemId, setItemId] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [frenteId, setFrenteId] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [duplicateAlertOpen, setDuplicateAlertOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Set default frente when data loads
  React.useEffect(() => {
    if (ultimaFrente && !frenteId && frentes.length > 0) {
      const exists = frentes.some(f => f.id === ultimaFrente);
      if (exists) setFrenteId(ultimaFrente);
    }
  }, [ultimaFrente, frentes, frenteId]);

  // Reset form when modal opens
  React.useEffect(() => {
    if (open) {
      setItemId('');
      setQuantidade('');
      setFrenteId(ultimaFrente && frentes.some(f => f.id === ultimaFrente) ? ultimaFrente : '');
      setObservacoes('');
      setFile(null);
      setSubmitting(false);
    }
  }, [open]);

  const selectedItem = useMemo(() => itens.find(i => i.id === itemId), [itens, itemId]);

  const canSubmit = !!itemId && !!frenteId && !!quantidade && parseFloat(quantidade) > 0 && !submitting;

  // Check for recent duplicate before submitting
  const checkDuplicateAndSubmit = useCallback(async () => {
    if (!canSubmit || !user) return;
    try {
      const twoMinAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();
      const { data: recent } = await supabase
        .from('emp_validacoes_execucao')
        .select('id')
        .eq('contrato_id', contratoId)
        .eq('planilha_item_id', itemId)
        .eq('registrado_por', user.id)
        .eq('quantidade_executada', parseFloat(quantidade))
        .gte('created_at', twoMinAgo)
        .limit(1);
      if (recent && recent.length > 0) {
        setDuplicateAlertOpen(true);
        return;
      }
    } catch {
      // If check fails, proceed anyway
    }
    await doSubmit();
  }, [canSubmit, user, contratoId, itemId, quantidade]);

  const doSubmit = async () => {
    if (!canSubmit || !user || !selectedItem) return;
    setSubmitting(true);

    try {
      // Log Quick Capture submit event (gated by feature flag)
      logUxEvent({
        contrato_id: contratoId,
        tipo_evento: 'quick_capture_submit',
        payload: {
          planilha_item_id: itemId,
          quantidade: parseFloat(quantidade),
          frente_id: frenteId,
          com_evidencia: !!file,
        },
      });

      const execResult = await createExecucao.mutateAsync({
        contrato_id: contratoId,
        planilha_item_id: itemId,
        frente_id: frenteId,
        data_execucao: new Date().toISOString().split('T')[0],
        quantidade_executada: parseFloat(quantidade),
        unidade: selectedItem.unidade,
        tipo: 'execucao',
        observacoes: observacoes.trim() || null,
      });

      // Register business event
      if (execResult?.id) {
        logUxEvent({
          contrato_id: contratoId,
          tipo_evento: 'execucao_registrada_quick_capture',
          entidade: 'execucao',
          entidade_id: execResult.id,
          payload: {
            planilha_item_id: itemId,
            quantidade: parseFloat(quantidade),
            frente_id: frenteId,
            com_evidencia: !!file,
          },
        });

        // Upload evidence if provided
        if (file) {
          await uploadEvidencia.mutateAsync({
            execucaoId: execResult.id,
            file,
            descricao: 'Quick Capture',
            categoria: 'execucao',
          });
        }
      }

      toast({
        title: 'Execução registrada',
        description: `Seguirá para Qualidade → Planejamento → Medição${!file ? ' · Sem evidência anexada' : ''}`,
      });
      onOpenChange(false);
    } catch (err) {
      // Error handled by mutation toasts
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Registro Rápido
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Item contratual */}
          <div className="space-y-1.5">
            <Label>Item contratual <span className="text-destructive">*</span></Label>
            <Select value={itemId} onValueChange={setItemId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecionar item..." />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {itens.map(item => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.codigo_item} — {item.descricao} ({item.unidade})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quantidade */}
          <div className="space-y-1.5">
            <Label>
              Quantidade <span className="text-destructive">*</span>
              {selectedItem && <span className="text-muted-foreground ml-1">({selectedItem.unidade})</span>}
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              placeholder="0,00"
              value={quantidade}
              onChange={e => setQuantidade(e.target.value)}
            />
          </div>

          {/* Frente — EXPLÍCITO e VISÍVEL */}
          <div className="space-y-1.5">
            <Label>Frente de trabalho <span className="text-destructive">*</span></Label>
            <Select value={frenteId} onValueChange={setFrenteId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecionar frente..." />
              </SelectTrigger>
              <SelectContent>
                {frentes.map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Foto (opcional) */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <Camera className="h-4 w-4" />
              Foto
              <span className="text-muted-foreground text-xs ml-1">(opcional)</span>
            </Label>
            <div
              className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? (
                <div className="flex items-center gap-2 justify-center text-sm">
                  <ImagePlus className="h-4 w-4 text-primary" />
                  <span className="truncate max-w-[200px]">{file.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  >
                    Remover
                  </Button>
                </div>
              ) : (
                <div className="text-muted-foreground text-sm">
                  <ImagePlus className="h-6 w-6 mx-auto mb-1 opacity-50" />
                  <p>Adicionar foto aumenta a rastreabilidade</p>
                  <p className="text-xs">(recomendado)</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) setFile(f);
                e.target.value = '';
              }}
            />
          </div>

          {/* Observação */}
          <div className="space-y-1.5">
            <Label>Observação <span className="text-muted-foreground text-xs">(opcional)</span></Label>
            <Textarea
              placeholder="Detalhes adicionais..."
              rows={2}
              value={observacoes}
              onChange={e => setObservacoes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={checkDuplicateAndSubmit} disabled={!canSubmit}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <Zap className="h-4 w-4 mr-1" />
                Registrar execução
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Duplicate alert */}
    <AlertDialog open={duplicateAlertOpen} onOpenChange={setDuplicateAlertOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Registro semelhante recente
          </AlertDialogTitle>
          <AlertDialogDescription>
            Um registro com o mesmo item e quantidade foi feito nos últimos 2 minutos. Deseja continuar mesmo assim?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setDuplicateAlertOpen(false); doSubmit(); }}>
            Registrar mesmo assim
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
